// src/components/Header.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  IconButton,
  Avatar,
  Typography,
  Tooltip,
  Menu,
  MenuItem,
  Divider,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "@mui/icons-material/Logout";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import defaultLogo from "../assets/logo.png";

const LOGO_BASE = "https://dgps.co.in/public/admin/assets/img/schools/logo";

export default function Header({ onToggle }) {
  const navigate = useNavigate();
  const [userName, setUserName] = useState("");
  const [avatarSrc, setAvatarSrc] = useState("");
  const [schoolLogo, setSchoolLogo] = useState(defaultLogo);
  const [schoolName, setSchoolName] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    try {
      setUserName(localStorage.getItem("name") || "");
      const avatar = localStorage.getItem("avatar");
      if (avatar) setAvatarSrc(avatar);

      const rawSchools = localStorage.getItem("schools");
      const savedSchoolId = localStorage.getItem("school_id");
      if (rawSchools) {
        const parsed = JSON.parse(rawSchools);
        const chosen =
          parsed.find((s) => String(s.id) === String(savedSchoolId)) || parsed[0];
        if (chosen) {
          setSchoolName((chosen.name || "").toUpperCase());
          if (chosen.logo) {
            let logo = chosen.logo;
            if (!/^https?:\/\//i.test(logo) && !logo.startsWith("data:")) {
              logo = `${LOGO_BASE}/${logo.replace(/^\/+/, "")}`;
            }
            setSchoolLogo(logo);
          }
        }
      } else {
        const sname = localStorage.getItem("school_name");
        const slog = localStorage.getItem("school_logo");
        if (sname) setSchoolName(sname.toUpperCase());
        if (slog) {
          let logo = slog;
          if (!/^https?:\/\//i.test(logo) && !logo.startsWith("data:")) {
            logo = `${LOGO_BASE}/${logo.replace(/^\/+/, "")}`;
          }
          setSchoolLogo(logo);
        }
      }
    } catch (e) {
      console.warn("Header init error", e);
    }
  }, []);

  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleLogout = () => {
    localStorage.clear();
    // localStorage.removeItem("token");
    // localStorage.removeItem("id");
    // localStorage.removeItem("name");
    // localStorage.removeItem("schools");
    // localStorage.removeItem("school_id");
    // localStorage.removeItem("role");
    // localStorage.removeItem("userType");
    window.location.href = "/app/login";
  };

  return (
    <Box
      component="header"
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        height: 72,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: { xs: 1.25, sm: 2.5, md: 3.5 },
        bgcolor: "rgba(255,255,255,0.92)",
        borderBottom: "1px solid rgba(37,99,235,0.12)",
        zIndex: 1400,
        boxShadow: "0 14px 36px rgba(23,32,51,0.12)",
        backdropFilter: "saturate(140%) blur(16px)",
        background:
          "linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(240,249,255,0.96) 46%, rgba(236,253,245,0.94) 100%)",
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(circle at 16% 0%, rgba(37,99,235,0.1), transparent 28%), radial-gradient(circle at 86% 0%, rgba(22,163,74,0.11), transparent 26%)",
          pointerEvents: "none",
        },
        "&::after": {
          content: '""',
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          height: 2,
          background: "#d7e3f1",
        },
      }}
    >
      {/* Left side - toggle + logo */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          minWidth: { xs: "auto", md: 220 },
        }}
      >
        <IconButton
          onClick={() => typeof onToggle === "function" && onToggle()}
          sx={{
            width: 44,
            height: 44,
            color: "#102a43",
            bgcolor: "rgba(37,99,235,0.08)",
            border: "1px solid rgba(37,99,235,0.14)",
            boxShadow: "0 10px 22px rgba(37,99,235,0.1)",
            "&:hover": {
              bgcolor: "rgba(37,99,235,0.14)",
              color: "#1d4ed8",
            },
          }}
        >
          <MenuIcon />
        </IconButton>
        <Box
          sx={{
            width: 52,
            height: 52,
            p: 0.55,
            borderRadius: 2,
            display: "grid",
            placeItems: "center",
            background:
              "linear-gradient(145deg, #ffffff, #e0f2fe)",
            border: "1px solid rgba(37,99,235,0.16)",
            boxShadow: "0 14px 28px rgba(23,32,51,0.12)",
            transition: "transform .18s ease, box-shadow .18s ease",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: "0 18px 34px rgba(37,99,235,0.18)",
            },
          }}
        >
          <Avatar
            src={schoolLogo}
            alt="school logo"
            onError={(e) => (e.currentTarget.src = defaultLogo)}
            sx={{
              width: "100%",
              height: "100%",
              borderRadius: 1.4,
              bgcolor: "#ffffff",
            }}
          />
        </Box>
      </Box>

      {/* Middle - school name */}
      <Box
        sx={{
          flex: 1,
          textAlign: "center",
          px: { xs: 1, sm: 2 },
          overflow: "hidden",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Typography
          variant="h6"
          noWrap
          sx={{
            maxWidth: "min(760px, 100%)",
            px: { xs: 1.5, sm: 3 },
            py: 0.9,
            borderRadius: 999,
            fontWeight: 900,
            letterSpacing: 0,
            fontSize: { xs: "0.9rem", sm: "1.05rem", md: "1.18rem" },
            color: "#102a43",
            textTransform: "uppercase",
            background:
              "linear-gradient(90deg, rgba(255,255,255,0.9), rgba(224,242,254,0.92), rgba(236,253,245,0.9))",
            border: "1px solid rgba(37,99,235,0.12)",
            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.9), 0 10px 28px rgba(37,99,235,0.08)",
            textShadow: "0 1px 0 rgba(255,255,255,0.7)",
          }}
        >
          {schoolName || "SCHOOL PORTAL"}
        </Typography>
      </Box>

      {/* Right side - user info */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 1,
          minWidth: { xs: "auto", md: 220 },
        }}
      >
        {!isMobile && (
          <Typography
            variant="body2"
            sx={{
              px: 1.5,
              py: 0.85,
              borderRadius: 999,
              fontWeight: 850,
              maxWidth: 150,
              color: "#102a43",
              bgcolor: "rgba(255,255,255,0.76)",
              border: "1px solid rgba(37,99,235,0.12)",
              boxShadow: "0 10px 24px rgba(23,32,51,0.08)",
            }}
            noWrap
          >
            {userName || "User"}
          </Typography>
        )}

        <Tooltip title="Account">
          <IconButton
            onClick={handleMenuOpen}
            sx={{
              p: 0.35,
              border: "1px solid rgba(37,99,235,0.16)",
              bgcolor: "rgba(255,255,255,0.86)",
              boxShadow: "0 12px 26px rgba(23,32,51,0.12)",
            }}
          >
            <Avatar
              src={avatarSrc || undefined}
              alt={userName || "user"}
              sx={{ width: 42, height: 42, bgcolor: "#2563eb", border: "2px solid #dbeafe" }}
            >
              {!avatarSrc && <AccountCircleIcon />}
            </Avatar>
          </IconButton>
        </Tooltip>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{
            sx: {
              minWidth: 190,
              p: 0,
              borderRadius: 2,
              overflow: "hidden",
              border: "1px solid rgba(78,102,135,0.16)",
              boxShadow: "0 18px 46px rgba(23,32,51,0.16)",
            },
          }}
        >
          <Box sx={{ px: 2, py: 1.25 }}>
            <Typography
              variant="subtitle2"
              noWrap
              sx={{ fontWeight: 700, color: "text.primary" }}
            >
              {userName || "User"}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "text.secondary" }}
              noWrap
            >
              {localStorage.getItem("role") || ""}
            </Typography>
          </Box>

          <Divider sx={{ my: 0 }} />

          <MenuItem sx={{ gap: 1 }}>
            <AccountCircleIcon fontSize="small" />
            <Typography variant="body2">Profile</Typography>
          </MenuItem>

          <MenuItem
            onClick={() => {
                localStorage.clear();
                navigate("/login");
              handleMenuClose();
              //handleLogout();
            }}
            sx={{ gap: 1 }}
          >
            <LogoutIcon fontSize="small" />
            <Typography variant="body2">Logout</Typography>
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
}
